import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NgxSpinnerService } from "ngx-spinner";
import Me from "../helpers/me";
import App from "../helpers/app";
import Api from "../helpers/api";
import { take } from "rxjs/operators";

interface Filters {
  municipio?: number;
  nome?: string;
  login?: string;
  periodoCadastroInicio?: string;
  periodoCadastroFim?: string;
}

interface Conferidor {
  id: number;
  nome: string;
  login: string;
  ativo: number;
  pode_excluir_matricula: number;
}

interface Municipio {
  id: number;
  nome: string;
  uf?: string;
}

interface NovoConferidor {
  nome: string;
  login: string;
  senha: string;
  confirmaSenha: string;
  id_cidades: number[];
  pode_excluir_matricula: boolean;
}

@Component({
  selector: 'app-conferidores',
  templateUrl: './conferidores.component.html',
  styleUrls: ['./conferidores.component.scss']
})
export class ConferidoresComponent implements OnInit {
  apiCall = this.api.new().silent();
  Math = Math; // Para usar Math.min no template
  
  // Paginação
  page = 1;
  pageSize = 7;
  total = 0;
  
  // Dados
  conferidores: Conferidor[] = [];
  municipios: Municipio[] = [];
  
  // Municípios com busca e paginação
  municipiosLoading = false;
  municipiosOffset = 0;
  municipiosLimit = 50;
  municipiosTotal = 0;
  municipiosSearchTerm = '';
  municipiosHasMore = true;
  
  // Paginação para modal de edição
  municipiosEdicaoOffset = 0;
  municipiosEdicaoTotal = 0;
  municipiosEdicaoSearchTerm = '';
  municipiosEdicaoHasMore = true;
  
  // Filtros
  filters: Filters = {
    municipio: null,
    nome: null,
    login: null,
    periodoCadastroInicio: null,
    periodoCadastroFim: null
  };
  
  // Ordenação
  currentSortColumn: string = '';
  currentSortDirection: string = 'asc';
  
  // Modal - Atualização de Senha
  atualizacaoSenha = {
    novaSenha: null,
    confirmaNovaSenha: null,
  };
  userToUpdate: Conferidor = null;
  
  // Modal - Novo Conferidor
  novoConferidor: NovoConferidor = {
    nome: null,
    login: null,
    senha: null,
    confirmaSenha: null,
    id_cidades: [],
    pode_excluir_matricula: false
  };
  municipiosSelecionados: number[] = [];
  
  // Modal - Editar Municípios
  conferidorEditando: Conferidor = null;
  municipiosConferidor: Municipio[] = [];
  municipiosSelecionadosEdicao: number[] = [];
  municipiosEdicaoLista: Municipio[] = []; // Lista específica para modal de edição

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute,
    private me: Me,
    private app: App,
    private api: Api
  ) {
  }

  async ngOnInit() {
    await this.loadMunicipios();
    await this.loadConferidores();
  }

  async loadMunicipios(nome: string = '', append: boolean = false) {
    this.municipiosLoading = true;
    try {
      const params: any = {
        limit: this.municipiosLimit,
        offset: append ? this.municipiosOffset : 0
      };
      
      if (nome && nome.trim() !== '') {
        params.nome = nome.trim();
      }

      const response = await this.apiCall.get('municipios', params).pipe(take(1)).toPromise();
      
      let novosMunicipios: Municipio[] = [];
      if (response && response.data) {
        novosMunicipios = response.data;
        this.municipiosTotal = response.total || response.data.length;
      } else if (Array.isArray(response)) {
        novosMunicipios = response;
        this.municipiosTotal = response.length;
      }

      if (append) {
        this.municipios = [...this.municipios, ...novosMunicipios];
      } else {
        this.municipios = novosMunicipios;
        this.municipiosOffset = 0;
      }

      this.municipiosOffset += novosMunicipios.length;
      this.municipiosHasMore = novosMunicipios.length === this.municipiosLimit;
      
    } catch (error) {
      console.error('Erro ao carregar municípios:', error);
    }
    this.municipiosLoading = false;
  }

  async onMunicipioSearch(event: { term: string }) {
    this.municipiosSearchTerm = event.term || '';
    this.municipiosOffset = 0;
    
    if (event.term && event.term.length >= 2) {
      await this.loadMunicipios(event.term, false);
    } else if (!event.term || event.term.length === 0) {
      await this.loadMunicipios('', false);
    }
  }

  async onMunicipioScrollToEnd() {
    if (!this.municipiosLoading && this.municipiosHasMore) {
      await this.loadMunicipios(this.municipiosSearchTerm, true);
    }
  }

  async loadConferidores() {
    const self = this;
    
    self.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });

    try {
      const params = this.buildSearchParams();
      const response = await self.apiCall.get('conferidores', params).pipe(take(1)).toPromise();
      
      if (response && response.data) {
        self.conferidores = response.data;
        self.total = response.total || response.data.length;
      } else if (Array.isArray(response)) {
        self.conferidores = response;
        self.total = response.length;
      } else {
        self.conferidores = [];
        self.total = 0;
      }
    } catch (error) {
      console.error('Erro ao carregar conferidores:', error);
      self.conferidores = [];
      self.total = 0;
    }

    self.spinner.hide();
  }

  buildSearchParams(): any {
    const offset = (this.page - 1) * this.pageSize;
    let params: any = {
      offset: offset,
      limit: this.pageSize,
      ativo: 1
    };

    if (this.filters.nome && this.filters.nome.trim() !== '') {
      params.nome = this.filters.nome.trim();
    }

    if (this.filters.login && this.filters.login.trim() !== '') {
      params.login = this.filters.login.trim();
    }

    if (this.filters.municipio) {
      params.id_cidade = this.filters.municipio;
    }

    return params;
  }

  // Converter formato de data de dd/mm/yyyy para yyyy-mm-dd
  convertDateFormat(dateString: string): string | null {
    if (!dateString) return null;
    
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  // Filtros
  applyFilters() {
    this.page = 1;
    this.loadConferidores();
  }

  clearFilters() {
    this.filters = {
      municipio: null,
      nome: null,
      login: null,
      periodoCadastroInicio: null,
      periodoCadastroFim: null
    };
    this.page = 1;
    this.currentSortColumn = '';
    this.currentSortDirection = 'asc';
    this.loadConferidores();
  }

  // Ordenação
  sortTable(column: string) {
    if (this.currentSortColumn === column) {
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = column;
      this.currentSortDirection = 'asc';
    }

    this.page = 1;
    this.loadConferidores();
  }

  getSortIcon(column: string): string {
    if (this.currentSortColumn === column) {
      return this.currentSortDirection === 'asc' ? 'fa-caret-up' : 'fa-caret-down';
    }
    return '';
  }

  isColumnSorted(column: string): boolean {
    return this.currentSortColumn === column;
  }

  // Paginação
  async onPageChange(page: number) {
    this.page = page;
    await this.loadConferidores();
  }

  // Modais
  openModal(content, conferidor: Conferidor = null) {
    this.userToUpdate = conferidor;
    this.atualizacaoSenha = {
      novaSenha: null,
      confirmaNovaSenha: null,
    };
    this.modalService.open(content, {
      centered: true,
      size: 'lg',
      backdrop: true,
      backdropClass: 'modal-backdrop-transparente',
      keyboard: true
    });
  }

  openCadastroModal(content) {
    this.novoConferidor = {
      nome: null,
      login: null,
      senha: null,
      confirmaSenha: null,
      id_cidades: [],
      pode_excluir_matricula: false
    };
    this.municipiosSelecionados = [];
    this.modalService.open(content, {
      centered: true,
      size: 'lg',
      backdrop: true,
      backdropClass: 'modal-backdrop-transparente',
      keyboard: true
    });
  }

  async openEditarMunicipiosModal(content, conferidor: Conferidor) {
    this.conferidorEditando = conferidor;
    this.municipiosConferidor = [];
    this.municipiosSelecionadosEdicao = [];
    this.municipiosEdicaoLista = [];
    this.municipiosEdicaoOffset = 0;
    this.municipiosEdicaoSearchTerm = '';
    this.municipiosEdicaoHasMore = true;

    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });

    try {
      // Buscar municípios vinculados ao conferidor
      const response = await this.apiCall.get(`conferidores/${conferidor.id}/municipios`).pipe(take(1)).toPromise();
      
      let municipiosDoConferidor: Municipio[] = [];
      if (response && response.data) {
        municipiosDoConferidor = response.data;
      } else if (Array.isArray(response)) {
        municipiosDoConferidor = response;
      }
      
      // Normalizar estrutura dos municípios do conferidor para o formato esperado pelo ng-select
      this.municipiosConferidor = municipiosDoConferidor.map((m: any) => ({
        id: m.id_cidade,  // Usar id_cidade como id do município
        nome: m.nome_cidade,  // Usar nome_cidade como nome
        uf: m.uf || ''
      }));

      // Carregar lista inicial de municípios para edição
      await this.loadMunicipiosEdicao('', false);
      
      // Definir os IDs selecionados
      this.municipiosSelecionadosEdicao = this.municipiosConferidor.map((m: Municipio) => m.id);

    } catch (error) {
      console.error('Erro ao carregar municípios do conferidor:', error);
    }

    this.spinner.hide();
    this.modalService.open(content, {
      centered: true,
      size: 'lg',
      backdrop: true,
      backdropClass: 'modal-backdrop-transparente',
      keyboard: true
    });
  }

  async loadMunicipiosEdicao(nome: string = '', append: boolean = false) {
    this.municipiosLoading = true;
    try {
      const params: any = {
        limit: this.municipiosLimit,
        offset: append ? this.municipiosEdicaoOffset : 0
      };
      
      if (nome && nome.trim() !== '') {
        params.nome = nome.trim();
      }

      const response = await this.apiCall.get('municipios', params).pipe(take(1)).toPromise();
      
      let novosMunicipios: Municipio[] = [];
      if (response && response.data) {
        novosMunicipios = response.data;
        this.municipiosEdicaoTotal = response.total || response.data.length;
      } else if (Array.isArray(response)) {
        novosMunicipios = response;
        this.municipiosEdicaoTotal = response.length;
      }

      if (append) {
        // Adicionar novos municípios à lista existente
        const idsExistentes = new Set(this.municipiosEdicaoLista.map(m => m.id));
        const novosUnicos = novosMunicipios.filter(m => !idsExistentes.has(m.id));
        this.municipiosEdicaoLista = [...this.municipiosEdicaoLista, ...novosUnicos];
      } else {
        // Nova busca - começa do zero mas mantém os do conferidor
        this.municipiosEdicaoLista = [...novosMunicipios];
        
        // Garantir que os municípios já selecionados continuem na lista
        for (const mun of this.municipiosConferidor) {
          if (!this.municipiosEdicaoLista.find(m => m.id === mun.id)) {
            this.municipiosEdicaoLista.unshift(mun);
          }
        }
        this.municipiosEdicaoOffset = 0;
      }

      this.municipiosEdicaoOffset += novosMunicipios.length;
      this.municipiosEdicaoHasMore = novosMunicipios.length === this.municipiosLimit;
      
    } catch (error) {
      console.error('Erro ao buscar municípios:', error);
    }
    this.municipiosLoading = false;
  }

  async onMunicipioSearchEdicao(event: { term: string }) {
    this.municipiosEdicaoSearchTerm = event.term || '';
    this.municipiosEdicaoOffset = 0;
    
    if (event.term && event.term.length >= 2) {
      await this.loadMunicipiosEdicao(event.term, false);
    } else if (!event.term || event.term.length === 0) {
      await this.loadMunicipiosEdicao('', false);
    }
  }

  async onMunicipioScrollToEndEdicao() {
    if (!this.municipiosLoading && this.municipiosEdicaoHasMore) {
      await this.loadMunicipiosEdicao(this.municipiosEdicaoSearchTerm, true);
    }
  }

  // CRUD
  async insertConferidor(modal) {
    const self = this;

    if (!self.novoConferidor.nome || !self.novoConferidor.login || !self.novoConferidor.senha) {
      await self.app.alert('Atenção!', 'Preencha todos os campos obrigatórios.', 'warning');
      return;
    }

    if (self.novoConferidor.senha !== self.novoConferidor.confirmaSenha) {
      await self.app.alert('Atenção!', 'As senhas não conferem.', 'warning');
      return;
    }

    self.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });

    try {
      const payload: any = {
        nome: self.novoConferidor.nome,
        login: self.novoConferidor.login,
        senha: self.novoConferidor.senha,
        ativo: 1,
        pode_excluir_matricula: self.novoConferidor.pode_excluir_matricula ? 1 : 0
      };

      if (self.municipiosSelecionados && self.municipiosSelecionados.length > 0) {
        payload.id_cidades = self.municipiosSelecionados;
      }

      const response = await self.apiCall.post('conferidores', payload).pipe(take(1)).toPromise();
      
      self.spinner.hide();
      
      if (response && response.success) {
        await self.app.alert('Sucesso!', 'Conferidor cadastrado com sucesso!', 'success');
        modal.close('Close click');
        await self.loadConferidores();
      } else {
        await self.app.alert('Erro!', response?.message || 'Erro ao cadastrar conferidor.', 'error');
      }
    } catch (error) {
      self.spinner.hide();
      console.error('Erro ao cadastrar conferidor:', error);
      await self.app.alert('Erro!', 'Erro ao cadastrar conferidor. Tente novamente.', 'error');
    }
  }

  async updateMunicipios(modal) {
    const self = this;

    if (!self.conferidorEditando?.id) {
      return;
    }

    self.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });

    try {
      const payload = {
        id_cidades: self.municipiosSelecionadosEdicao
      };

      const response = await self.apiCall.put(`conferidores/${self.conferidorEditando.id}/municipios`, payload).pipe(take(1)).toPromise();

      self.spinner.hide();
      
      if (response && response.success) {
        await self.app.alert('Sucesso!', 'Municípios atualizados com sucesso!', 'success');
        modal.close('Close click');
        await self.loadConferidores();
      } else {
        await self.app.alert('Erro!', response?.message || 'Erro ao atualizar municípios.', 'error');
      }
    } catch (error) {
      self.spinner.hide();
      console.error('Erro ao atualizar municípios:', error);
      await self.app.alert('Erro!', 'Erro ao atualizar municípios. Tente novamente.', 'error');
    }
  }

  async updateSenha(modal) {
    const self = this;

    if (!self.userToUpdate?.id || !self.atualizacaoSenha.novaSenha) {
      return;
    }

    if (self.atualizacaoSenha.novaSenha !== self.atualizacaoSenha.confirmaNovaSenha) {
      await self.app.alert('Atenção!', 'As senhas não conferem.', 'warning');
      return;
    }

    self.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });

    try {
      const response = await self.apiCall.put(`conferidores/${self.userToUpdate.id}/senha`, {
        senha: self.atualizacaoSenha.novaSenha
      }).pipe(take(1)).toPromise();

      self.spinner.hide();
      
      if (response && response.success) {
        await self.app.alert('Sucesso!', 'Senha atualizada com sucesso!', 'success');
        modal.close('Close click');
      } else {
        await self.app.alert('Erro!', response?.message || 'Erro ao atualizar senha.', 'error');
      }
    } catch (error) {
      self.spinner.hide();
      console.error('Erro ao atualizar senha:', error);
      await self.app.alert('Erro!', 'Erro ao atualizar senha. Tente novamente.', 'error');
    }
  }

  async alternarPermissaoExclusao(conferidor: Conferidor) {
    const habilitar = Number(conferidor.pode_excluir_matricula) !== 1;
    const confirmado = await this.app.confirm(
      habilitar ? 'Liberar edição e exclusão de matrículas' : 'Revogar edição e exclusão de matrículas',
      habilitar
        ? `Deseja permitir que "${conferidor.nome}" edite e exclua matrículas durante a conferência?`
        : `Deseja retirar de "${conferidor.nome}" a permissão para editar e excluir matrículas?`,
      habilitar ? 'Liberar' : 'Revogar'
    );
    if (!confirmado) return;

    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });

    try {
      const response: any = await this.apiCall.put(`conferidores/${conferidor.id}`, {
        pode_excluir_matricula: habilitar ? 1 : 0
      }).pipe(take(1)).toPromise();
      if (!response || response.success !== true) {
        throw new Error(response && response.error && response.error.message
          ? response.error.message
          : 'Não foi possível atualizar a permissão.');
      }
      conferidor.pode_excluir_matricula = habilitar ? 1 : 0;
      await this.app.alert(
        'Sucesso!',
        habilitar ? 'Permissão de exclusão liberada.' : 'Permissão de exclusão revogada.',
        'success'
      );
    } catch (error) {
      await this.app.alert(
        'Erro!',
        error && error.message ? error.message : 'Não foi possível atualizar a permissão.',
        'error'
      );
    } finally {
      this.spinner.hide();
    }
  }

  async deleteConferidor(conferidor: Conferidor) {
    const self = this;

    const confirmResult = await self.app.confirm(
      'Confirmar Inativação',
      `Tem certeza que deseja inativar o conferidor "${conferidor.nome}"? Ele não poderá mais acessar o sistema.`,
      'Inativar'
    );

    if (confirmResult) {
      self.spinner.show(undefined, {
        type: 'ball-triangle-path',
        size: 'medium',
        bdColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        fullScreen: true
      });

      try {
        const response = await self.apiCall.delete(`conferidores/${conferidor.id}`).pipe(take(1)).toPromise();
        
        self.spinner.hide();
        
        if (response && response.success) {
          await self.app.alert('Sucesso!', 'Conferidor inativado com sucesso!', 'success');
          await self.loadConferidores();
        } else {
          await self.app.alert('Erro!', response?.message || 'Erro ao inativar conferidor.', 'error');
        }
      } catch (error) {
        self.spinner.hide();
        console.error('Erro ao inativar conferidor:', error);
        await self.app.alert('Erro!', 'Erro ao inativar conferidor. Tente novamente.', 'error');
      }
    }
  }
}
